<script>
import { setPageTitle } from "../utils/helpers";
import _ from "lodash";
import navigation from "../components/global/navigation.vue";
import ArtworkIntakeUpsell from "../components/artwork-intake/ArtworkIntakeUpsell.vue";
import modalAddJob from "../components/modals/modal-add-job.vue";
import modalGatedFeatures from "../components/modals/modal-non-premium-integration-screen.vue";
import { JOB_STATUS, MODAL_NAMES, PREMIUM_FEATURES_KEYS } from "../utils/constants";

const STATUS_LABEL = {
  new: "New",
  viewed: "Viewed",
  archived: "Archived",
  converted_to_proof: "Converted to Proof",
  converted_to_job: "Converted to Job",
};

const MANUAL_STATUSES = new Set(["new", "viewed", "archived"]);

const KB = 1024;

const FILE_KIND_MAP = [
  { match: /^image\/jpeg/, label: "JPG", typeName: "JPEG image", color: "#7c3aed" },
  { match: /^image\/png/, label: "PNG", typeName: "PNG image", color: "#7c3aed" },
  { match: /^image\//, label: "IMG", typeName: "Image", color: "#7c3aed" },
  { match: /^application\/pdf$/, label: "PDF", typeName: "PDF", color: "#dc2626" },
  { match: /(postscript|illustrator|ai$)/i, label: "AI", typeName: "Adobe Illustrator", color: "#ea580c" },
  { match: /(photoshop|psd)/i, label: "PSD", typeName: "Adobe Photoshop", color: "#2563eb" },
  { match: /(word|docx|doc$)/i, label: "DOC", typeName: "Word document", color: "#1d4ed8" },
  { match: /^text\//, label: "TXT", typeName: "Text", color: "#6b7280" },
];

function fileKind(mime, name) {
  const test = mime || "";
  const ext = (name && name.split(".").pop()) || "";
  for (const k of FILE_KIND_MAP) {
    if (k.match.test(test) || k.match.test(ext)) return k;
  }
  return {
    label: ext.slice(0, 4).toUpperCase() || "FILE",
    typeName: ext ? `${ext.toUpperCase()} file` : "File",
    color: "#475569",
  };
}

export default {
  name: "ArtworkIntakeDetails",
  components: { navigation, ArtworkIntakeUpsell, modalAddJob, modalGatedFeatures },
  props: ["id"],
  data() {
    return {
      isLoading: true,
      submission: null,
      isUpdatingStatus: false,
      isConverting: false,
      isDownloadingAll: false,
      jobStatuses: [],
      MODAL_NAMES,
      PREMIUM_FEATURES_KEYS,
    };
  },
  computed: {
    isPremiumAccount() {
      const sku = _.get(this, "$A.Auth.account.subscriptionState.sku", "");
      return sku.includes("standard") || sku.includes("premium") || sku.includes("enterprise");
    },
    canUseJobs() {
      const sku = _.get(this, "$A.Auth.account.subscriptionState.sku", "");
      return sku.includes("premium") || sku.includes("enterprise");
    },
    submissionId() {
      return this.id || this.$route.params.id;
    },
    totalFileSize() {
      if (!this.submission) return 0;
      return this.submission.files.reduce((sum, f) => sum + (f.size || 0), 0);
    },
    canConvertToJob() {
      return !!this.submission && !this.submission.convertedJobId;
    },
    canConvertToProof() {
      return !!this.submission && !this.submission.convertedProofId;
    },
  },
  methods: {
    formatDate(iso) {
      return this.$A.FormatDateToString(iso);
    },
    formatDateTime(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    },
    statusLabel(status) {
      return STATUS_LABEL[status] || status;
    },
    formatSize(bytes) {
      if (bytes == null) return "";
      if (bytes < KB) return `${bytes} B`;
      if (bytes < KB * KB) return `${(bytes / KB).toFixed(1)} KB`;
      return `${(bytes / (KB * KB)).toFixed(2)} MB`;
    },
    downloadFile(file) {
      // The presigned URL carries an attachment content-disposition, so an
      // in-place navigation saves the file instead of rendering it in a tab.
      const a = document.createElement("a");
      a.href = file.url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
    async downloadAll() {
      if (!this.submission || this.isDownloadingAll) return;
      this.isDownloadingAll = true;
      try {
        await this.$A.ArtworkIntakeService.downloadSubmissionFilesZip(this.submission.id);
      } catch (e) {
        console.error("Failed to download files:", e);
        this.$A.AlertUser("Failed to download files");
      }
      this.isDownloadingAll = false;
    },
    fieldChips(field) {
      const v = field.value;
      if (Array.isArray(v)) return v.filter((x) => x !== "" && x != null);
      if (v == null || v === "") return [];
      return [String(v)];
    },
    fileKind(file) {
      return fileKind(file.mimeType, file.name);
    },
    isImage(file) {
      return /^image\//.test(file.mimeType || "");
    },
    canTransition(status) {
      return this.submission && MANUAL_STATUSES.has(this.submission.status) && status !== this.submission.status;
    },
    async setStatus(status) {
      if (!this.submission || this.isUpdatingStatus) return;
      this.isUpdatingStatus = true;
      try {
        await this.$A.ArtworkIntakeService.updateSubmissionStatus(this.submission.id, status);
        this.submission.status = status;
      } catch (e) {
        console.error("Failed to update status:", e);
      }
      this.isUpdatingStatus = false;
    },
    suggestedJobName() {
      const fields = this.submission?.customFieldValues || [];
      for (const f of fields) {
        if (f.dataType === "Single Line Text" && Array.isArray(f.value) && f.value[0]) {
          return String(f.value[0]).trim();
        }
      }
      const who = this.submission?.contact?.name || this.submission?.contact?.email || "customer";
      return `Artwork from ${who}`;
    },
    buildJobDescriptionFromSubmission() {
      if (!this.submission) return "";
      const c = this.submission.contact || {};
      const parts = [];
      parts.push(`<p><strong>From:</strong> ${this.escapeHtml(c.name || "")}${c.email ? ` &lt;${this.escapeHtml(c.email)}&gt;` : ""}</p>`);
      const fields = this.submission.customFieldValues || [];
      const rows = fields
        .map((f) => {
          const vals = Array.isArray(f.value) ? f.value.filter((x) => x !== "" && x != null) : [];
          if (!vals.length) return null;
          return `<li><strong>${this.escapeHtml(f.label)}:</strong> ${this.escapeHtml(vals.join(", "))}</li>`;
        })
        .filter(Boolean);
      if (rows.length) parts.push(`<ul>${rows.join("")}</ul>`);
      return parts.join("");
    },
    escapeHtml(s) {
      return String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    defaultJobStatusId() {
      if (!this.jobStatuses.length) return null;
      const notStarted = this.jobStatuses.find(s => s.statusCategoryID === JOB_STATUS.NOT_STARTED);
      return notStarted?.id || this.jobStatuses[0]?.id || null;
    },
    async openConvertToJobModal() {
      if (!this.canConvertToJob) return;
      if (!this.canUseJobs) {
        this.$bvModal.show(MODAL_NAMES.GATED_FEATURES_MODAL);
        return;
      }
      if (!this.jobStatuses.length) {
        try { this.jobStatuses = await this.$A.JobStatusService.getAllJobStatuses() || []; }
        catch { this.jobStatuses = []; }
      }
      const submissionContact = this.submission?.contact || {};
      const seedContacts = submissionContact.id
        ? [{ id: submissionContact.id, name: submissionContact.name, email: submissionContact.email }]
        : [];
      const seed = {
        id: null,
        name: this.suggestedJobName(),
        description: this.buildJobDescriptionFromSubmission(),
        status: this.defaultJobStatusId(),
        priority: 0,
        startDate: null,
        endDate: null,
        assignees: [],
        tags: [],
        contacts: seedContacts,
      };
      const presetFiles = (this.submission.files || []).map((f) => ({
        id: f.id,
        name: f.name,
        size: f.size,
        url: f.url,
      }));
      this.$refs.modalAddJob.openWithJob(seed, { presetFiles });
    },
    createProofFromSubmission() {
      if (!this.canConvertToProof) return;
      window.$A.filesForProof = (this.submission.files || []).map((f) => ({
        name: f.name,
        sizeBytes: f.size,
        mimeType: f.mimeType,
        urlSource: f.url,
      }));
      window.$A.proofCustomFieldsToPrefill = (this.submission.customFieldValues || []).map((f) => ({
        accountProofPropertyId: f.accountProofPropertyId,
        dataType: f.dataType,
        value: Array.isArray(f.value) ? f.value : [],
      }));
      window.$A.proofSubmissionIdToLink = this.submission.id;
      this.$router.push("/create-proof");
    },
    async handleJobCreatedFromSubmission(newJob) {
      if (!this.submission || this.isConverting) return;
      this.isConverting = true;
      try {
        const jobRequest = {
          name: newJob.name || null,
          description: newJob.description || null,
          assignees: Array.isArray(newJob.assignees) ? newJob.assignees : [],
          status: newJob.status || null,
          priority: newJob.priority || null,
          startDate: newJob.startDate || null,
          endDate: newJob.endDate || null,
          tags: {
            update: Array.isArray(newJob.tags) ? newJob.tags : [],
            delete: [],
          },
        };
        const res = await this.$A.ArtworkIntakeService.convertSubmissionToJob(
          this.submission.id,
          jobRequest
        );

        if (res && res.jobId && Array.isArray(newJob._pendingContactIds) && newJob._pendingContactIds.length) {
          try {
            await this.$A.JobService.updateContactLink({
              jobId: res.jobId,
              update: newJob._pendingContactIds,
              delete: [],
            });
          } catch (e) {
            console.error("Failed to attach contacts to converted job:", e);
          }
        }

        this.$toast("Job created from submission", {
          title: "Success",
          variant: "success",
        });
        this.$router.push(`/job-details/${res.jobId}`);
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || "Failed to create job";
        console.error("Failed to convert submission to job:", e);
        this.$toast(msg, {
          title: "Error",
          variant: "danger",
        });
      }
      this.isConverting = false;
    },
  },
  async mounted() {
    if (!this.isPremiumAccount) {
      this.isLoading = false;
      return;
    }
    try {
      this.submission = await this.$A.ArtworkIntakeService.getSubmission(this.submissionId);
      const who = this.submission?.contact?.name || this.submission?.contact?.email;
      setPageTitle(who ? `Submission from ${who}` : "Submission");
    } catch (e) {
      console.error("Failed to load submission:", e);
    }
    this.isLoading = false;
  },
};
</script>

<template>
  <div class="nav-margin">
    <navigation />
    <ArtworkIntakeUpsell v-if="!isPremiumAccount" />
    <template v-else>
      <div class="back-btn bg-white border-bottom border-gray-100 d-flex gap-12 justify-content-between flex-wrap py-3 px-3">
        <a class="p-0 d-flex align-items-center gap-1 fs-14" href="/artwork-intake">
          <img src="/img/chevron-left-gray.svg" alt="chevron-left" />
          Back
        </a>
      </div>

      <main v-if="!isLoading && submission" class="flex-fill overflow-auto details-section">
        <div class="px-3 py-32 w-100">
          <div class="details-container mx-auto">
            <div class="d-flex flex-column gap-3 details-header">
              <div>
                <h6 class="mb-2 text-gray-500 fs-12 fw-normal">Submission #{{ submission.id }}</h6>
                <h1 class="text-gray-900 fw-semibold mb-0 fs-18">{{ submission.contact.name }}</h1>
                <a class="text-gray-700 fs-14 d-inline-block mt-1" :href="`mailto:${submission.contact.email}`">
                  {{ submission.contact.email }}
                </a>
              </div>

              <div class="d-flex align-items-center flex-wrap gap-2 toolbar-list">
                <span class="status-chip" :class="'status-' + submission.status">
                  {{ statusLabel(submission.status) }}
                </span>
                <button
                  v-if="canTransition('viewed')"
                  class="chip-btn"
                  :disabled="isUpdatingStatus"
                  @click="setStatus('viewed')"
                >Mark as Viewed</button>
                <button
                  v-if="canTransition('archived')"
                  class="chip-btn"
                  :disabled="isUpdatingStatus"
                  @click="setStatus('archived')"
                >Archive</button>
                <button
                  v-if="submission.status === 'archived'"
                  class="chip-btn"
                  :disabled="isUpdatingStatus"
                  @click="setStatus('viewed')"
                >Unarchive</button>
                <span class="meta-dot" aria-hidden="true">·</span>
                <span class="meta-text">Received {{ formatDateTime(submission.submittedAt) }}</span>
              </div>

              <div v-if="submission.convertedJobId" class="meta-row">
                <span class="meta-text">
                  Converted to
                  <router-link :to="`/job-details/${submission.convertedJobId}`" class="converted-link">
                    Job {{ submission.convertedJobNumber || `#${submission.convertedJobId}` }}
                  </router-link>
                </span>
              </div>

              <div v-if="submission.convertedProofId" class="meta-row">
                <span class="meta-text">
                  Converted to
                  <router-link :to="`/proof-item?id=${submission.convertedProofId}`" class="converted-link">
                    Proof{{ submission.convertedProofName ? `: ${submission.convertedProofName}` : ` #${submission.convertedProofId}` }}
                  </router-link>
                </span>
              </div>
            </div>

            <section
              v-if="submission.customFieldValues && submission.customFieldValues.length"
              class="details-section-block"
            >
              <div class="section-head">
                <h2 class="section-title">Form fields</h2>
              </div>

              <dl class="spec-list">
                <div
                  v-for="field in submission.customFieldValues"
                  :key="field.accountProofPropertyId"
                  class="spec-row"
                >
                  <dt class="spec-label">{{ field.label }}</dt>
                  <dd class="spec-value">
                    <template v-if="fieldChips(field).length">
                      <span class="spec-chip" v-for="chip in fieldChips(field)" :key="chip">{{ chip }}</span>
                    </template>
                    <span v-else class="text-gray-500">—</span>
                  </dd>
                </div>
              </dl>
            </section>

            <section class="details-section-block">
              <div class="section-head">
                <div class="section-head-left">
                  <h2 class="section-title">Files</h2>
                  <span class="section-count">{{ submission.files.length }} &middot; {{ formatSize(totalFileSize) }}</span>
                </div>
                <div class="d-flex align-items-center gap-2 flex-wrap">
                  <button
                    v-if="submission.files.length > 1"
                    class="chip-btn"
                    @click="downloadAll"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Download all
                  </button>
                  <button
                    v-if="canConvertToProof && submission.files.length"
                    class="chip-btn chip-btn-primary"
                    :disabled="isConverting || isUpdatingStatus"
                    @click="createProofFromSubmission"
                  >Create Proof from Submission</button>
                  <button
                    v-if="canConvertToJob"
                    class="chip-btn chip-btn-primary"
                    :disabled="isConverting || isUpdatingStatus"
                    @click="openConvertToJobModal"
                  >Create Job from Submission</button>
                </div>
              </div>

              <div v-if="!submission.files.length" class="section-empty">No files attached.</div>

              <ul v-else class="file-list">
                <li
                  v-for="file in submission.files"
                  :key="file.id || file.name"
                  class="file-item"
                  @click="downloadFile(file)"
                  role="button"
                  tabindex="0"
                  @keyup.enter="downloadFile(file)"
                >
                  <div class="file-icon">
                    <div v-if="isImage(file)" class="file-thumb">
                      <img :src="file.url" :alt="file.name" loading="lazy" />
                    </div>
                    <div v-else class="file-tag" :style="{ background: fileKind(file).color }">
                      {{ fileKind(file).label }}
                    </div>
                  </div>
                  <div class="file-meta">
                    <div class="file-name" :title="file.name">{{ file.name }}</div>
                    <div class="file-sub">{{ fileKind(file).typeName }}</div>
                  </div>
                  <div class="file-size">{{ formatSize(file.size) }}</div>
                  <div class="file-download" aria-label="Download">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                  </div>
                </li>
              </ul>
            </section>

          </div>
        </div>
      </main>

      <div v-else-if="!isLoading" class="not-found">
        <h3>Submission not found</h3>
        <a href="/artwork-intake">Back to submissions</a>
      </div>

      <div v-else class="loading-screen">Loading submission...</div>

      <modalAddJob
        ref="modalAddJob"
        @job-created="handleJobCreatedFromSubmission"
      />
      <modalGatedFeatures :feature-key="PREMIUM_FEATURES_KEYS.JOB_MANAGEMENT" :modalId="MODAL_NAMES.GATED_FEATURES_MODAL" />
    </template>
  </div>
</template>

<style scoped>
.loading-screen,
.not-found {
  padding: 64px 24px;
  text-align: center;
  color: #6b7280;
}
.not-found h3 {
  margin-bottom: 12px;
}

.details-section {
  background: #fff;
}

.details-container {
  max-width: 880px;
}

.details-header {
  margin-bottom: 32px;
}

.toolbar-list {
  margin-top: 4px;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  background: #f1f3f5;
  color: #374151;
  border: 1px solid #e5e7eb;
  line-height: 1;
}
.status-chip.status-new { background: #ecfdf5; color: #019256; border-color: #bbf7d0; }
.status-chip.status-viewed { background: #eff6ff; color: #2563eb; border-color: #dbeafe; }
.status-chip.status-archived { background: #f3f4f6; color: #6b7280; border-color: #e5e7eb; }
.status-chip.status-converted_to_proof,
.status-chip.status-converted_to_job { background: #faf5ff; color: #7c3aed; border-color: #e9d5ff; }

.chip-btn {
  height: 26px;
  padding: 0 10px;
  border-radius: 6px;
  background: #fff;
  color: #374151;
  border: 1px solid #e5e7eb;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 120ms ease, border-color 120ms ease;
}
.chip-btn:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #d1d5db;
}
.chip-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.chip-btn-primary {
  background: #019255;
  color: #fff;
  border-color: #019255;
}
.chip-btn-primary:hover:not(:disabled) {
  background: #017a47;
  border-color: #017a47;
}

.converted-link {
  color: #6b7280;
  text-decoration: underline;
}
.converted-link:hover {
  color: #374151;
}

.meta-dot {
  color: #d1d5db;
  margin: 0 2px;
}
.meta-text {
  color: #6b7280;
  font-size: 12px;
}

.details-section-block {
  border-top: 1px solid #f1f3f5;
  padding-top: 24px;
  margin-top: 24px;
}

.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}
.section-head-left {
  display: flex;
  align-items: baseline;
  gap: 10px;
  min-width: 0;
}
.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}
.section-count {
  font-size: 12px;
  color: #6b7280;
}
.chip-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.section-empty {
  font-size: 13px;
  color: #6b7280;
}

/* Files */
.file-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.file-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  cursor: pointer;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  transition: background-color 120ms ease, border-color 120ms ease, transform 120ms ease;
}
.file-item:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}
.file-icon {
  flex-shrink: 0;
}
.file-tag {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
}
.file-thumb {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  overflow: hidden;
  background: #f3f4f6;
}
.file-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.file-meta {
  flex: 1;
  min-width: 0;
}
.file-name {
  font-size: 14px;
  font-weight: 500;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.file-sub {
  margin-top: 1px;
  font-size: 12px;
  color: #6b7280;
}
.file-size {
  flex-shrink: 0;
  white-space: nowrap;
  font-size: 12px;
  color: #6b7280;
  text-align: right;
  min-width: 64px;
}
.file-download {
  flex-shrink: 0;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  transition: color 120ms ease, background-color 120ms ease;
}
.file-item:hover .file-download {
  color: #019256;
  background: rgba(1, 146, 86, 0.08);
}

/* Form fields */
.spec-list {
  margin: 0;
}
.spec-row {
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 16px;
  padding: 10px 0;
  border-top: 1px solid #f1f3f5;
}
.spec-row:first-child {
  border-top: 0;
  padding-top: 0;
}
.spec-label {
  font-size: 12px;
  color: #6b7280;
  margin: 0;
  padding-top: 4px;
}
.spec-value {
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.spec-chip {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  background: #f3f4f6;
  color: #1f2937;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
  max-width: 100%;
  overflow-wrap: anywhere;
  white-space: normal;
}

@media (max-width: 640px) {
  .details-container {
    padding: 0 4px;
  }
  .spec-row {
    grid-template-columns: 1fr;
    gap: 4px;
  }
  .spec-label {
    padding-top: 0;
  }
  .file-size {
    display: none;
  }
  .file-download {
    opacity: 1;
  }
}
</style>
